import { useEffect, useRef } from 'react';
import type { OcrFeedEvent, OcrProgressEvent } from '../lib/api';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// OcrActivityFeedPanel — live scrolling log of a running OCR digestion batch.
//
// Wave 6: MatchCardLive.tsx needs the exact same live-feed rendering the OCR
// Lab (MatchCardDemo.tsx) already has, but that page's `formatEvent` +
// `ActivityFeedPanel` are private, unexported functions and MatchCardDemo.tsx
// must not be touched (Lab stays byte-for-byte as-is per the wave brief) —
// so this is a fresh, independent re-implementation with the same event
// formatting/behaviour, lifted out into a shared component both pages could
// import from in future. Known duplication with MatchCardDemo.tsx's private
// copy is a minor follow-up (not fixed here — see PR notes).
//
// Visual language matches MatchCardLive's other panels (rounded-xl border
// iron-800/60 card shell), not the OCR Lab's own bg-paper-elevated styling —
// this file only renders on the real match page.
// ─────────────────────────────────────────────────────────────────────────────

function shortField(f: string): string {
  return ({ score: 'score', objScore: 'obj', kills: 'K', deaths: 'D', time: 't', plants: 'plants', overloads: 'OL' } as Record<string, string>)[f] ?? f;
}

export function formatEvent(e: OcrProgressEvent): { line: string; tone: 'info' | 'good' | 'warn' | 'bad' } {
  switch (e.kind) {
    case 'BATCH_STARTED':       return { line: `► batch started — ${e.shotsTotal} shots`, tone: 'info' };
    case 'SHOT_ENHANCED':       return { line: `· ${e.shotId.slice(-12)}: enhanced ${e.sizeBeforeKb}KB→${e.sizeAfterKb}KB (${e.ms}ms)`, tone: 'info' };
    case 'SHOT_QUALITY':        return { line: `· ${e.shotId.slice(-12)}: quality=${e.quality.toFixed(2)}${e.flags?.length ? ` [${e.flags.join(',')}]` : ''}`, tone: e.quality < 0.4 ? 'warn' : 'info' };
    case 'SHOT_STAGE1_START':   return { line: `→ ${e.shotId.slice(-12)}: Stage 1 OCR…`, tone: 'info' };
    case 'SHOT_STAGE1_DONE':    return { line: `✓ ${e.shotId.slice(-12)}: parsed ${e.rowsParsed} rows · ${e.tokensIn}/${e.tokensOut}tk · ${e.ms}ms${e.mode ? ` · ${e.mode}` : ''}${e.mapName ? ` @ ${e.mapName}` : ''}`, tone: 'good' };
    case 'SHOT_STAGE1_ERROR':   return { line: `✗ ${e.shotId.slice(-12)}: Stage 1 ERROR — ${e.error}`, tone: 'bad' };
    case 'SHOT_NAMES_SNAPPED':  return { line: `~ ${e.shotId.slice(-12)}: ${e.snapsApplied} name${e.snapsApplied === 1 ? '' : 's'} snapped`, tone: 'info' };
    case 'SHOT_ROSTER_CHECKED': {
      const parts: string[] = [];
      if (e.unexpectedNames.length > 0) parts.push(`${e.unexpectedNames.length} unexpected: ${e.unexpectedNames.join(', ')}`);
      if (e.missingRosterMembers.length > 0) parts.push(`${e.missingRosterMembers.length} missing: ${e.missingRosterMembers.join(', ')}`);
      return parts.length > 0
        ? { line: `⚠ ${e.shotId.slice(-12)}: roster check — ${parts.join(' · ')}`, tone: 'warn' }
        : { line: `· ${e.shotId.slice(-12)}: roster check clean`, tone: 'info' };
    }
    case 'CONSENSUS_GAME_DONE': return { line: `✓ G${e.gameNum} consensus: ${e.cellsAgreed} agreed, ${e.cellsDisputed} disputed`, tone: e.cellsDisputed > 0 ? 'warn' : 'good' };
    case 'STAGE2_QUEUED':       return { line: `→ Stage 2: ${e.cellsCount} disputed cell${e.cellsCount === 1 ? '' : 's'} queued for re-read`, tone: 'warn' };
    case 'STAGE2_CELL_RESOLVED':return { line: `✓ Stage 2 G${e.gameNum} ${e.playerKey}.${shortField(e.field)}: ${e.before} → ${e.after} (${e.ms}ms)`, tone: 'good' };
    case 'STAGE2_CELL_KEPT':    return { line: `· Stage 2 G${e.gameNum} ${e.playerKey}.${shortField(e.field)}: kept ${e.value}${e.reason ? ` (${e.reason})` : ''}`, tone: e.reason ? 'warn' : 'info' };
    case 'BATCH_COMPLETE':      return { line: `■ batch complete in ${(e.totalMs / 1000).toFixed(1)}s · S1=${e.stage1Calls} S2=${e.stage2Calls} · cost ~$${e.estimatedUsd.toFixed(4)}`, tone: 'good' };
    case 'MANUAL_ROUTED':       return { line: `⚠ routed to MANUAL entry — ${e.message}`, tone: 'warn' };
    case 'BATCH_FAILED':        return { line: `✗ BATCH FAILED — ${e.message}`, tone: 'bad' };
    case 'SLOT_PERSIST_FAILED': return { line: `⚠ some results failed to save, card held for review — ${e.message}`, tone: 'bad' };
    // AC2: pipeline-flagged extraction quality issue — card held at DISPUTED
    // rather than auto-VERIFIED. This kind was previously missing from this
    // switch entirely (lab-crash-fix root cause: no case AND no default here
    // meant formatEvent fell through and returned `undefined`, crashing the
    // `{ line, tone } = formatEvent(...)` destructure below mid-batch).
    case 'QUALITY_DISPUTED':    return { line: `⚠ ${e.message}`, tone: 'bad' };
    // Roster-integrity flag surfacing (Lisa product ruling, 2026-07-21) —
    // OBSERVE-ONLY visibility, not a dispute/error signal in its own right
    // (the card's own DISPUTED/VERIFIED outcome is untouched by this flag),
    // so this renders muted ('info', not 'warn'/'bad') — typography only,
    // no colour-coded alarm strip for something that's purely informational.
    case 'SUBMITTER_NOT_ROSTERED': return { line: e.message, tone: 'info' };
    default: {
      // Defence in depth for whatever kind we don't recognise yet — a future
      // backend event this frontend hasn't been updated for, or a malformed
      // feed row. Never let one bad row take the whole live feed down; skip
      // to a safe fallback line instead of throwing.
      const kind = (e as { kind?: unknown } | undefined)?.kind;
      return { line: `· unrecognised event${typeof kind === 'string' ? ` (${kind})` : ''}`, tone: 'info' };
    }
  }
}

export function OcrActivityFeedPanel({ events, batchInFlight, clientLog = [] }: {
  events: OcrFeedEvent[];
  batchInFlight: boolean;
  clientLog?: string[];
}) {
  const ref = useRef<HTMLPreElement>(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [events.length, clientLog.length]);

  if (events.length === 0 && clientLog.length === 0 && !batchInFlight) return null;

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display text-xs font-bold uppercase tracking-wider text-iron-300">Live activity feed</h3>
        <span className="font-mono text-[10px] text-iron-500">
          {batchInFlight ? <span className="text-emerald-400">● streaming</span> : 'idle'} · {events.length} server events · auto-cleans after 1h
        </span>
      </div>
      <pre ref={ref} className="max-h-72 overflow-y-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-iron-300">
        {clientLog.map((l, i) => <div key={`c${i}`} className="text-iron-400">{l}</div>)}
        {events.length === 0 && clientLog.length === 0 && (
          <div className="text-iron-500">waiting for the first event…</div>
        )}
        {events.map(e => {
          const { line, tone } = formatEvent(e.event);
          const ts = new Date(e.ts).toISOString().slice(11, 23);
          const toneCls = tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : tone === 'bad' ? 'text-red-300' : 'text-iron-200';
          return <div key={e.sk} className={cn(toneCls)}><span className="text-iron-500">{ts}</span>  {line}</div>;
        })}
      </pre>
    </section>
  );
}
